import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { cart as cartApi } from './api';
import { useAuth } from './auth-context';
import { variantKey } from './variant';

const CartContext = createContext(null);

const sameLine = (item, productId, selectedVariant) =>
  (item.product_id || item.product?.id) === productId
  && variantKey(item.selected_variant) === variantKey(selectedVariant);

const cartSignature = (cartItems = []) =>
  [...cartItems]
    .map((item) => {
      const pid = item.product_id || item.product?.id || '';
      const qty = Math.max(1, Number(item.quantity) || 1);
      return `${pid}:${variantKey(item.selected_variant)}:${qty}`;
    })
    .filter((s) => !s.startsWith(':'))
    .sort()
    .join('|');

const normalizeCartItems = (cartItems = []) =>
  cartItems
    .map((item) => ({
      product_id: item.product_id || item.product?.id,
      quantity: Math.max(1, Number(item.quantity) || 1),
      product: item.product || null,
      selected_variant: item.selected_variant || null,
    }))
    .filter((i) => i.product_id);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const fetchCart = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await cartApi.get();
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const mergeGuestCartAndFetch = useCallback(async () => {
    try {
      setLoading(true);
      // Check if there's a guest cart to merge
      const guestCart = localStorage.getItem('cart');
      if (guestCart) {
        const guestItems = JSON.parse(guestCart);
        // Add each guest item to the user's cart
        for (const item of guestItems) {
          try {
            await cartApi.add({
              product_id: item.product_id,
              quantity: item.quantity,
              selected_variant: item.selected_variant || null,
            });
          } catch (e) {
            console.log('Could not merge item:', item.product_id);
          }
        }
        // Clear guest cart after merge
        localStorage.removeItem('cart');
      }
      // Fetch the merged cart (loading already true)
      const response = await cartApi.get();
      setItems(response.data.items || []);
    } catch (error) {
      console.error('Failed to merge cart:', error);
      try {
        const response = await cartApi.get();
        setItems(response.data.items || []);
      } catch (e) {
        console.error('Failed to fetch cart:', e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      // Merge guest cart with user cart when logging in
      mergeGuestCartAndFetch();
    } else {
      // Load from localStorage for guests and fetch product details
      const loadGuestCart = async () => {
        const stored = localStorage.getItem('cart');
        if (stored) {
          const storedItems = JSON.parse(stored);
          // If items have product details, use them directly
          if (storedItems.length > 0 && storedItems[0].product) {
            setItems(storedItems);
          } else {
            // Otherwise, we need to keep the stored items with product info
            setItems(storedItems);
          }
        }
      };
      loadGuestCart();
    }
  }, [user, mergeGuestCartAndFetch]);

  const addItem = async (product, quantity = 1, selectedVariant = null) => {
    const variant = selectedVariant || null;
    if (user) {
      try {
        await cartApi.add({
          product_id: product.id,
          quantity,
          selected_variant: variant,
        });
        await fetchCart();
      } catch (error) {
        console.error('Failed to add to cart:', error);
      }
    } else {
      const existingIndex = items.findIndex((i) => sameLine(i, product.id, variant));
      let newItems;
      if (existingIndex >= 0) {
        newItems = items.map((item, idx) => (
          idx === existingIndex ? { ...item, quantity: item.quantity + quantity, selected_variant: variant } : item
        ));
      } else {
        newItems = [...items, { product_id: product.id, quantity, product, selected_variant: variant }];
      }
      setItems(newItems);
      localStorage.setItem('cart', JSON.stringify(newItems));
    }
  };

  const updateItem = async (productId, quantity, selectedVariant = null) => {
    const variant = selectedVariant || null;
    if (user) {
      try {
        await cartApi.update({
          product_id: productId,
          quantity,
          selected_variant: variant,
        });
        await fetchCart();
      } catch (error) {
        console.error('Failed to update cart:', error);
      }
    } else {
      let newItems;
      if (quantity <= 0) {
        newItems = items.filter((i) => !sameLine(i, productId, variant));
      } else {
        newItems = items.map((i) => (
          sameLine(i, productId, variant) ? { ...i, quantity } : i
        ));
      }
      setItems(newItems);
      localStorage.setItem('cart', JSON.stringify(newItems));
    }
  };

  const removeItem = async (productId, selectedVariant = null) => {
    await updateItem(productId, 0, selectedVariant);
  };

  const clearCart = async () => {
    if (user) {
      try {
        await cartApi.clear();
        setItems([]);
      } catch (error) {
        console.error('Failed to clear cart:', error);
      }
    } else {
      setItems([]);
      localStorage.removeItem('cart');
    }
  };

  /** Ödeme başarısız / tekrar dene: sepeti verilen kalemlerle değiştir (üzerine yaz) */
  const restoreCartItems = useCallback(async (cartItems) => {
    if (!Array.isArray(cartItems) || cartItems.length === 0) return;
    // Aynı içerik zaten sepetteyse API clear/add döngüsüne girme (badge flicker + sayfa donması)
    if (cartSignature(itemsRef.current) === cartSignature(cartItems)) {
      return;
    }
    const normalized = normalizeCartItems(cartItems);
    if (!normalized.length) return;

    // Önce UI'yi güncelle — badge 0↔1↔2 oynamasın
    setItems(normalized);
    if (!user) {
      localStorage.setItem('cart', JSON.stringify(normalized));
      return;
    }

    try {
      try {
        await cartApi.clear();
      } catch (e) {
        console.warn('Cart clear before restore failed', e);
      }
      for (const item of normalized) {
        try {
          await cartApi.add({
            product_id: item.product_id,
            quantity: item.quantity,
            selected_variant: item.selected_variant || null,
          });
        } catch (e) {
          console.warn('Could not restore cart item:', item.product_id, e);
        }
      }
      // silent: cartLoading flip → Checkout useEffect döngüsünü önler
      await fetchCart({ silent: true });
    } catch (error) {
      console.error('Failed to restore cart:', error);
    }
  }, [user, fetchCart]);

  // Helper function to get the applicable wholesale price
  const getWholesalePrice = (product, quantity) => {
    if (!product?.wholesale_tiers || product.wholesale_tiers.length === 0) {
      return product?.price || 0;
    }
    
    // Sort tiers by min_qty descending to find the best match
    const sortedTiers = [...product.wholesale_tiers].sort((a, b) => b.min_qty - a.min_qty);
    
    for (const tier of sortedTiers) {
      if (quantity >= tier.min_qty) {
        // Check if within max_qty (if specified)
        if (!tier.max_qty || quantity <= tier.max_qty) {
          return tier.price;
        }
      }
    }
    
    return product?.price || 0;
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => {
    const price = getWholesalePrice(item.product, item.quantity);
    return sum + (price * item.quantity);
  }, 0);

  return (
    <CartContext.Provider value={{ 
      items, 
      loading, 
      addItem, 
      updateItem, 
      removeItem, 
      clearCart,
      restoreCartItems,
      totalItems,
      totalPrice,
      getWholesalePrice,
      fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
